import { observer } from 'mobx-react';
import React, { useCallback } from 'react';
import ItemSize from '../../components/ItemSize/ItemSize';
import itemsStore from '../../store/itemsStore';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { MEASURE, measureValues } from 'src/constants/main';
import theme from 'src/constants/styles';
import integrationsStore from 'src/mobx/integrationStore';
import { localeStore } from 'src/mobx/localesStore';
import { TBooleanInNumber } from 'src/types/main';
import { booleanNumberReverse, isDisabledByPos } from 'src/utils/helpers';
import { Checkbox } from 'src/view/components/Controls/Checkbox/Checkbox';
import Icon from 'src/view/components/Controls/Icon/Icon';
import Switcher from 'src/view/components/Controls/Switcher/Switcer';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
import IntegrationTooltip from 'src/view/components/IntegrationTooltip/IntegrationTooltip';
import Text, { TEXT } from 'src/view/components/Text/Text';
import MenuManagTabItemPriceHeader from 'src/view/screens/MenuManagementScreen/components/MenuManagTabItemPriceHeader/MenuManagTabItemPriceHeader';
import MenuManagTabItemCalories from 'src/view/screens/MenuManagementScreen/containers/MenuManagTabItemCalories/MenuManagTabItemCalories';
import MenuManagTabItemPrice from 'src/view/screens/MenuManagementScreen/containers/MenuManagTabItemPrice/MenuManagTabItemPrice';
import { getMeasureOption } from '~/helpers';
import { InputWithPickerInside } from '~/view/components/Controls/Inputs';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';
import './style.scss';
import { TOOLTIP_POSITION } from '~/view/components/Controls/CustomTooltip/CustomTooltip';
import TooltipIcon from '~/view/components/TooltipIcon/TooltipIcon';

const ItemSizes: React.FC = observer((): JSX.Element | null => {
  const clickSizesSwitcher = (is_size: TBooleanInNumber) => {
    if (!itemsStore.item?.sizes.length) itemsStore.addSize(true);
    itemsStore.changeItemHandler({ is_size });

    // update validation
    if (!itemsStore.validation.price || itemsStore.validation.sizes.length) itemsStore.setValidation();
  };

  const changeMeasureValue = useCallback((weight: string | undefined) => {
    itemsStore.changeItemHandler({ weight });
  }, []);

  const changeMeasureLabel = useCallback((options: IPickerOption[]) => {
    if (!options.length) return;
    itemsStore.changeItemHandler({ unit: options[0].value as MEASURE });
  }, []);

  const changePrice = useCallback((price: string) => {
    itemsStore.changeItemHandler({ price });
  }, []);

  const changeCalories = useCallback((calories: number | null) => {
    itemsStore.changeItemHandler({ calories });
  }, []);

  const changeAllPlaces = useCallback(() => {
    itemsStore.changeItemHandler({ apply_item_price_to_all: booleanNumberReverse(itemsStore.item?.apply_item_price_to_all) });
  }, [itemsStore.item?.apply_item_price_to_all]);

  if (!itemsStore.item) return null;

  const {
    globalItemPrices: priceRestrictionAccess,
    itemSizeToggle: sizeToggleRestrictionAccess,
    itemPortion: portionRestrictionAccess,
    // Not exist for now
    itemCalories: caloriesRestrictionAccess,
  } = integrationsStore.posRestrictions || {};

  return (
    <div className="d-flex flex-column mt-6">
      <Text type={TEXT.SECTION_HEADER} text="item_prices_and_sizes_block_header" className="mb-4" />
      {/* sizes switcher */}
      <IntegrationManager setting={sizeToggleRestrictionAccess} wrapperClassName="d-flex flex-row align-items-center">
        <IntegrationTooltip isVisible={isDisabledByPos(sizeToggleRestrictionAccess)}>
          <Switcher status={itemsStore.item?.is_size || 0} onChange={clickSizesSwitcher} />
        </IntegrationTooltip>
        <Text type={TEXT.SWITCHER_LABEL} text="use_sizes" disabled={isDisabledByPos(sizeToggleRestrictionAccess)} />
      </IntegrationManager>
      {!itemsStore.item.is_size ? (
        <div className="d-flex flex-row my-4">
          {/* measure */}
          <IntegrationManager setting={portionRestrictionAccess} wrapperClassName="d-flex flex-column mr-3">
            <Text type={TEXT.BLOCK_HEADER} text="portion" disabled={isDisabledByPos(portionRestrictionAccess)} />
            <IntegrationTooltip isVisible={isDisabledByPos(portionRestrictionAccess)}>
              <InputWithPickerInside
                type="floatNumber"
                value={itemsStore.item.weight}
                onChangeValue={changeMeasureValue}
                disableOnFocusOnInputWrapper
                className="item-sizes__input-portion py-0 mt-2"
                contentClassName="full-width"
                pickerProps={{
                  options: measureValues.map(getMeasureOption),
                  selectedOptions: [getMeasureOption(itemsStore.item.unit)],
                  onSelect: changeMeasureLabel,
                }}
              />
            </IntegrationTooltip>
          </IntegrationManager>
          {/* calories */}
          <MenuManagTabItemCalories
            calories={itemsStore.item?.calories}
            title="kcal_input_field_label"
            validation={itemsStore.validation.calories}
            posRestriction={caloriesRestrictionAccess}
            changeCalories={changeCalories}
            setValidation={() => itemsStore.setValidation()}
            headerClassName="item-sizes__input-calories mb-2"
            inputClassName="item-sizes__input-calories"
            wrapperClassName="mr-3"
          />
          {/* price */}
          <MenuManagTabItemPrice
            price={itemsStore.item?.price}
            title="txt_price"
            validation={itemsStore.validation.price}
            posRestriction={priceRestrictionAccess}
            changePrice={changePrice}
            setValidation={() => itemsStore.setValidation()}
            headerClassName="item-sizes__input-price mb-2"
            inputClassName="item-sizes__input-price"
          />
        </div>
      ) : (
        <div className="d-flex flex-column">
          {/* sizes */}
          {itemsStore.item.sizes.length ? (
            <div className="d-flex flex-row mt-4">
              <Text type={TEXT.BLOCK_HEADER} text="size_name" className="item-size__input-name full-width" required />
              <Text type={TEXT.BLOCK_HEADER} text="portion" className="item-size__input-portion ml-3 full-width" />
              <div className="d-flex align-items-center item-size__input-calories ml-3">
                <Text type={TEXT.BLOCK_HEADER} text="kcal_input_field_label" className="mr-1" />
                <TooltipIcon message="ap_item_calories_field_infotip" position={TOOLTIP_POSITION.PORTAL} iconSize={16} />
              </div>
              <MenuManagTabItemPriceHeader title="txt_price" className="item-size__input-price ml-3 full-width" />
            </div>
          ) : null}
          <ul className="d-flex flex-column mb-4">
            {itemsStore.item.sizes.map((size) => (
              <ItemSize key={size.id} size={size} />
            ))}
          </ul>
          {/* add size button */}
          <div className="item-sizes__btn-add-wrapper mb-4">
            <button className="item-sizes__btn-add" onClick={() => itemsStore.addSize()}>
              <div className="item-sizes__btn-add-icon">
                <Icon name={Icons.Plus} size={11} color={theme.colors.mainblue} />
              </div>
              <p className="family-medium text-size-16 color-mainblue ml-2">{localeStore.t('create_new_size')}</p>
            </button>
          </div>
        </div>
      )}
      {/* checkbox */}
      {!itemsStore.item.is_draft ? (
        <Checkbox
          value={Boolean(itemsStore.item?.apply_item_price_to_all)}
          name="apply_item_price_to_all"
          label="apply_item_price_to_all"
          onClick={changeAllPlaces}
        />
      ) : null}
    </div>
  );
});

export default ItemSizes;
